import { useState } from 'react';
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Search, ExternalLink, Copy, Check, RefreshCw, Users, HelpCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { findBestMatches, ScoredMatch } from '@/utils/textSimilarity';

interface StripeCustomer {
  id: string;
  email?: string | null;
  name?: string | null;
  created: number;
  productIds: string[];
  subscriptionCount: number;
  activeSubscriptions: number;
  hasActiveSubscription: boolean;
  isPremium: boolean;
}

interface CustomerDetails {
  customer: {
    id: string;
    email?: string | null;
    name?: string | null;
    created: number;
    balance: number;
    currency?: string | null;
    delinquent: boolean;
    metadata: Record<string, string>;
  };
  subscriptions: Array<{
    id: string;
    status: string;
    created: number;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
    canceledAt?: number | null;
    items: Array<{
      productName: string;
      amount?: number | null;
      currency: string;
      interval?: string;
    }>;
  }>;
  paymentMethods: Array<{
    id: string;
    type: string;
    card?: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    } | null;
  }>;
  invoices: Array<{
    id: string;
    number?: string | null;
    status?: string | null;
    created: number;
    amountDue: number;
    amountPaid: number;
    currency: string;
    hostedInvoiceUrl?: string | null;
  }>;
}

interface SyncResult {
  stripeCustomerId: string;
  email: string | null;
  name: string | null;
  matched: boolean;
  appUserId?: string;
  premiumProducts: string[];
  error?: string;
}

export default function StripeCustomers() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'canceled' | 'premium'>('all');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [startingAfter, setStartingAfter] = useState<string | null>(null);
  const [pageHistory, setPageHistory] = useState<string[]>([]);
  const [showSyncResults, setShowSyncResults] = useState(false);
  const [syncResults, setSyncResults] = useState<{
    totalCustomers: number;
    matched: number;
    unmatched: number;
    results: SyncResult[];
  } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [premiumHelpOpen, setPremiumHelpOpen] = useState(false);
  const [linkingCustomer, setLinkingCustomer] = useState<{ customerId: string; email: string | null } | null>(null);
  const [linkUserId, setLinkUserId] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [availableUsers, setAvailableUsers] = useState<Array<{ user_id: string; email: string; name: string }>>([]);

  // List customers query
  const { data: listData, isLoading: isLoadingList } = useQuery({
    queryKey: ['stripe-customers', statusFilter, startingAfter],
    queryFn: async () => {
      const params = new URLSearchParams({ 
        limit: '10',
      });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (startingAfter) {
        params.append('starting_after', startingAfter);
      }
      
      const { data, error } = await supabase.functions.invoke(`list-all-stripe-customers?${params.toString()}`);
      
      if (error) throw error;
      return data as { customers: StripeCustomer[]; has_more: boolean; last_customer_id: string | null };
    },
  });

  // Search customers query
  const { data: searchData, isLoading: isSearching } = useQuery({
    queryKey: ['stripe-customers-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return null;
      
      const params = new URLSearchParams({ q: searchQuery.trim(), limit: '50' });
      const { data, error } = await supabase.functions.invoke(`list-stripe-customers?${params.toString()}`);
      
      if (error) throw error;
      return data as { customers: StripeCustomer[] };
    },
    enabled: searchQuery.trim().length > 0,
  });

  // Customer details query
  const { data: customerDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['stripe-customer-details', selectedCustomerId],
    queryFn: async () => {
      if (!selectedCustomerId) return null;
      
      const params = new URLSearchParams({ customer_id: selectedCustomerId });
      const { data, error } = await supabase.functions.invoke(`get-stripe-customer?${params.toString()}`);
      
      if (error) throw error;
      return data as CustomerDetails;
    },
    enabled: !!selectedCustomerId,
  });

  const customers = searchQuery.trim() ? searchData?.customers : listData?.customers;
  const isLoading = searchQuery.trim() ? isSearching : isLoadingList;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  const handleNextPage = () => {
    if (listData?.has_more && listData?.last_customer_id) {
      setPageHistory([...pageHistory, startingAfter || '']);
      setStartingAfter(listData.last_customer_id);
    }
  };

  const handlePreviousPage = () => {
    if (pageHistory.length > 0) {
      const newHistory = [...pageHistory];
      const previousCursor = newHistory.pop();
      setPageHistory(newHistory);
      setStartingAfter(previousCursor || null);
    }
  };

  // Sync Stripe products mutation
  const syncProductsMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-stripe-products');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Products Synced',
        description: `Synced ${data.total_products} Stripe products (${data.new_products} new)`,
      });
      queryClient.invalidateQueries({ queryKey: ['stripe-customers'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync Stripe products',
        variant: 'destructive',
      });
    },
  });

  // Sync all premium subscribers mutation
  const syncPremiumMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-all-premium-subscribers');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setSyncResults({
        totalCustomers: data.totalCustomers,
        matched: data.matched,
        unmatched: data.unmatched,
        results: data.results
      });
      setShowSyncResults(true);
      queryClient.invalidateQueries({ queryKey: ['stripe-customers'] });
      
      toast({
        title: 'Sync Complete',
        description: `Synced ${data.matched} premium subscribers`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync premium subscribers',
        variant: 'destructive',
      });
    },
  });

  const getStatusBadge = (customer: StripeCustomer) => {
    if (customer.isPremium) {
      return <Badge variant="default" className="bg-purple-600">Premium</Badge>;
    } else if (customer.activeSubscriptions > 0) {
      return <Badge variant="default" className="bg-green-600">Active</Badge>;
    } else if (customer.subscriptionCount > 0) {
      return <Badge variant="secondary">Canceled</Badge>;
    }
    return <Badge variant="outline">No Subscription</Badge>;
  };

  const handleManualLink = async () => {
    if (!linkingCustomer || !linkUserId.trim()) {
      toast({
        title: 'Error',
        description: 'Please select a user',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('link-stripe-customer', {
        body: { userId: linkUserId.trim(), stripeCustomerId: linkingCustomer.customerId }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Linked customer to user. Run sync to update subscriptions.`,
      });

      setLinkingCustomer(null);
      setLinkUserId('');
      setUserSearchQuery('');
      queryClient.invalidateQueries({ queryKey: ['stripe-customers'] });
    } catch (error: any) {
      toast({
        title: 'Failed to link',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  // Fetch users when dialog opens
  const fetchUsers = async () => {
    try {
      // Get all users with billing sync records
      const { data: billingData, error } = await supabase
        .from('user_billing_sync')
        .select('user_id')
        .limit(1000);

      if (error) throw error;

      const userIds = billingData?.map(b => b.user_id) || [];
      
      // Get user details from auth
      const usersPromise = userIds.map(async (userId) => {
        const { data: { user } } = await supabase.auth.admin.getUserById(userId);
        return user ? {
          user_id: userId,
          email: user.email || 'Unknown',
          name: user.user_metadata?.name || user.user_metadata?.full_name || 'Unknown'
        } : null;
      });

      const users = (await Promise.all(usersPromise)).filter(u => u !== null) as Array<{ user_id: string; email: string; name: string }>;
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
    }
  };

  // Fetch users when linking dialog opens
  React.useEffect(() => {
    if (linkingCustomer) {
      fetchUsers();
      // Pre-fill search with customer email if available
      if (linkingCustomer.email) {
        setUserSearchQuery(linkingCustomer.email);
      }
    }
  }, [linkingCustomer]);

  return (
    <>
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              What Does "Sync Products" Do?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="space-y-4 text-base">
            <div>
              <h4 className="font-semibold text-foreground mb-2">In Simple Terms:</h4>
              <p>
                The "Sync Products" button downloads your product list from Stripe and saves it to your app's database. 
                This lets you see all your Stripe products here in the admin panel.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">Why Do This?</h4>
              <p>
                Your app needs to know which products exist in Stripe so you can select which ones should give users "premium" access. 
                Without syncing, your app wouldn't know what products you have available in Stripe.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">When Should I Use It?</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>When you first set up your app</li>
                <li>After you create new products in Stripe</li>
                <li>If the product list looks outdated</li>
              </ul>
            </div>
            
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2">The Process:</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Connects to your Stripe account</li>
                <li>Downloads all your products (name, ID, description, status)</li>
                <li>Saves them to your app's database</li>
                <li>Now you can select which products grant premium access</li>
              </ol>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>

      <Dialog open={premiumHelpOpen} onOpenChange={setPremiumHelpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              What Does "Sync Premium Subscribers" Do?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="space-y-4 text-base">
            <div>
              <h4 className="font-semibold text-foreground mb-2">In Simple Terms:</h4>
              <p>
                This button goes through all your Stripe customers and matches them with users in your app. 
                It checks who has active subscriptions to your premium products and updates their premium status in your app's database.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">Why Do This?</h4>
              <p>
                Sometimes users might have paid in Stripe but their premium access isn't showing in your app. 
                This sync makes sure everyone who's paying for premium in Stripe actually has premium access in your app.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">When Should I Use It?</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>When users report they paid but don't have premium access</li>
                <li>After manually creating subscriptions in Stripe</li>
                <li>To do a bulk check of all premium users</li>
                <li>If you suspect sync issues between Stripe and your app</li>
              </ul>
            </div>
            
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2">The Process:</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Gets all customers from Stripe who have active subscriptions</li>
                <li>Tries to match each Stripe customer with a user in your app (by email)</li>
                <li>For matched users, creates or updates their billing records</li>
                <li>Saves their active subscriptions to your app's database</li>
                <li>Shows you a summary of matched and unmatched customers</li>
              </ol>
            </div>
            
            <div className="border-l-4 border-primary pl-4">
              <p className="text-sm">
                <strong>Note:</strong> Unmatched customers usually mean someone paid in Stripe but hasn't created an account in your app yet, 
                or they used a different email address.
              </p>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
      
      {/* Manual Link Dialog */}
      <Dialog open={!!linkingCustomer} onOpenChange={(open) => {
        if (!open) {
          setLinkingCustomer(null);
          setLinkUserId('');
          setUserSearchQuery('');
        }
      }}>
        <DialogContent className="max-w-full max-h-[95vh] w-[95vw] md:max-w-4xl overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Link Stripe Customer to App User</DialogTitle>
            <DialogDescription>
              Search and select an app user to link to this Stripe customer. The system will suggest the most likely matches.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto min-h-0">
            <div>
              <label className="text-sm font-medium">Stripe Customer</label>
              <div className="mt-1 space-y-1 p-3 bg-muted rounded-md">
                <p className="text-sm font-mono break-all">{linkingCustomer?.customerId}</p>
                <p className="text-sm text-muted-foreground">{linkingCustomer?.email || 'No email'}</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Search App Users</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, name, or user ID..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {userSearchQuery.trim() && (
                <p className="text-xs text-muted-foreground mt-1">
                  Using intelligent matching: exact matches, contains, and similarity search
                </p>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              {availableUsers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  Loading users...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead>Match</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="hidden md:table-cell">User ID</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const query = userSearchQuery.trim();
                        
                        // If no query, show all users
                        if (!query) {
                          return availableUsers.map((user) => (
                            <TableRow 
                              key={user.user_id}
                              className={linkUserId === user.user_id ? 'bg-muted' : ''}
                            >
                              <TableCell></TableCell>
                              <TableCell className="font-medium break-all">{user.email}</TableCell>
                              <TableCell className="break-all">{user.name}</TableCell>
                              <TableCell className="font-mono text-xs hidden md:table-cell break-all">
                                {user.user_id.substring(0, 16)}...
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant={linkUserId === user.user_id ? 'default' : 'outline'}
                                  onClick={() => setLinkUserId(user.user_id)}
                                >
                                  {linkUserId === user.user_id ? (
                                    <>
                                      <Check className="h-4 w-4 mr-1" />
                                      Selected
                                    </>
                                  ) : (
                                    'Select'
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ));
                        }

                        // Use fuzzy matching to find top 3 matches
                        const matches = findBestMatches(
                          availableUsers,
                          query,
                          (user) => [user.email, user.name, user.user_id],
                          3
                        );

                        if (matches.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                No matching users found. Try a different search term.
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return matches.map((match, index) => {
                          const user = match.item;
                          const matchTypeLabels = {
                            exact: 'Exact',
                            contains: 'Contains',
                            similar: 'Similar'
                          };
                          const matchTypeColors = {
                            exact: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                            contains: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                            similar: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          };
                          
                          return (
                            <TableRow 
                              key={user.user_id}
                              className={`${linkUserId === user.user_id ? 'bg-muted' : ''} ${index === 0 ? 'border-l-4 border-l-primary' : ''}`}
                            >
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <Badge className={`text-xs ${matchTypeColors[match.matchType]}`}>
                                    {matchTypeLabels[match.matchType]}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {(match.score * 100).toFixed(0)}%
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium break-all">{user.email}</TableCell>
                              <TableCell className="break-all">{user.name}</TableCell>
                              <TableCell className="font-mono text-xs hidden md:table-cell break-all">
                                {user.user_id.substring(0, 16)}...
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant={linkUserId === user.user_id ? 'default' : (index === 0 ? 'default' : 'outline')}
                                  onClick={() => setLinkUserId(user.user_id)}
                                >
                                  {linkUserId === user.user_id ? (
                                    <>
                                      <Check className="h-4 w-4 mr-1" />
                                      Selected
                                    </>
                                  ) : (
                                    index === 0 ? 'Best Match' : 'Select'
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-4 border-t flex-shrink-0">
              <p className="text-sm text-muted-foreground">
                {linkUserId ? '✓ User selected' : 'Select a user from the list above'}
              </p>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button className="flex-1 sm:flex-none" variant="outline" onClick={() => {
                  setLinkingCustomer(null);
                  setLinkUserId('');
                  setUserSearchQuery('');
                }}>
                  Cancel
                </Button>
                <Button className="flex-1 sm:flex-none" onClick={handleManualLink} disabled={!linkUserId}>
                  Link Customer
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Stripe Customers</CardTitle>
            <CardDescription>
              Manage and view all Stripe customers and their subscriptions
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncProductsMutation.mutate()}
              disabled={syncProductsMutation.isPending}
            >
              {syncProductsMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Products
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setHelpOpen(true)}>
              <HelpCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => syncPremiumMutation.mutate()}
              disabled={syncPremiumMutation.isPending}
            >
              {syncPremiumMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Users className="h-4 w-4 mr-2" />
              )}
              Sync Premium Subscribers
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setPremiumHelpOpen(true)}>
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search and Filters */}
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('all')}
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'premium' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('premium')}
            >
              Premium
            </Button>
            <Button
              variant={statusFilter === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('active')}
            >
              Active
            </Button>
            <Button
              variant={statusFilter === 'canceled' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('canceled')}
            >
              Canceled
            </Button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Subscriptions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers && customers.length > 0 ? (
                    customers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        className="cursor-pointer"
                        onClick={() => setSelectedCustomerId(customer.id)}
                      >
                        <TableCell>
                          <a
                            href={`https://dashboard.stripe.com/customers/${customer.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-primary hover:underline flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {customer.id.substring(0, 12)}...
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </TableCell>
                        <TableCell className="font-medium">
                          {customer.email || <span className="text-muted-foreground italic">No email</span>}
                        </TableCell>
                        <TableCell>
                          {customer.name || <span className="text-muted-foreground italic">No name</span>}
                        </TableCell>
                        <TableCell>
                          {formatDistanceToNow(new Date(customer.created * 1000), { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          {customer.subscriptionCount > 0 ? (
                            <span>
                              {customer.activeSubscriptions} active / {customer.subscriptionCount} total
                            </span>
                          ) : (
                            <span className="text-muted-foreground italic">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(customer)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://dashboard.stripe.com/customers/${customer.id}`, '_blank');
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No customers found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {!searchQuery.trim() && (
              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={pageHistory.length === 0}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  {customers?.length || 0} customers shown
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!listData?.has_more}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Sync Results Dialog */}
      <Dialog open={showSyncResults} onOpenChange={setShowSyncResults}>
        <DialogContent className="max-w-full max-h-[95vh] w-[95vw] md:max-w-6xl overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Premium Subscriber Sync Results</DialogTitle>
            <DialogDescription>
              Results from syncing Stripe customers with premium subscriptions to app users
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          {syncResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{syncResults.totalCustomers}</div>
                    <div className="text-sm text-muted-foreground">Total Premium Customers</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{syncResults.matched}</div>
                    <div className="text-sm text-muted-foreground">Successfully Linked</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-600">{syncResults.unmatched}</div>
                    <div className="text-sm text-muted-foreground">Not Matched</div>
                  </CardContent>
                </Card>
              </div>

              {/* Unmatched Customers List */}
              {syncResults.unmatched > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold">Customers Not Matched to App Users</h3>
                  <div className="border rounded-lg overflow-x-auto">
                     <Table>
                       <TableHeader>
                         <TableRow>
                           <TableHead>Customer ID</TableHead>
                           <TableHead>Email</TableHead>
                           <TableHead>Name</TableHead>
                           <TableHead>Premium Products</TableHead>
                           <TableHead>Reason</TableHead>
                           <TableHead>Action</TableHead>
                         </TableRow>
                       </TableHeader>
                       <TableBody>
                         {syncResults.results
                           .filter(r => !r.matched)
                           .map((result) => (
                             <TableRow key={result.stripeCustomerId}>
                               <TableCell>
                                 <a
                                   href={`https://dashboard.stripe.com/customers/${result.stripeCustomerId}`}
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   className="font-mono text-xs text-primary hover:underline flex items-center gap-1 break-all"
                                 >
                                   {result.stripeCustomerId.substring(0, 12)}...
                                   <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                 </a>
                               </TableCell>
                               <TableCell className="font-medium break-all">
                                 {result.email || <span className="text-muted-foreground italic">No email</span>}
                               </TableCell>
                               <TableCell className="break-all">
                                 {result.name || <span className="text-muted-foreground italic">No name</span>}
                               </TableCell>
                               <TableCell>
                                 <Badge variant="outline">{result.premiumProducts.length} products</Badge>
                               </TableCell>
                               <TableCell className="text-sm text-muted-foreground break-all">
                                 {result.error || 'Unknown error'}
                               </TableCell>
                               <TableCell>
                                 <div className="flex gap-2">
                                   <Button
                                     size="sm"
                                     variant="outline"
                                     onClick={() => {
                                       navigator.clipboard.writeText(result.stripeCustomerId);
                                       toast({
                                         title: 'Copied!',
                                         description: 'Customer ID copied to clipboard',
                                       });
                                     }}
                                   >
                                     <Copy className="h-4 w-4" />
                                   </Button>
                                   <Button
                                     size="sm"
                                     onClick={() => setLinkingCustomer({ customerId: result.stripeCustomerId, email: result.email })}
                                   >
                                     Link to User
                                   </Button>
                                 </div>
                               </TableCell>
                             </TableRow>
                           ))}
                       </TableBody>
                     </Table>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    These customers have premium subscriptions in Stripe but no matching app account.
                    <strong className="block mt-2">To manually link:</strong> Click "Link to User" and search for the matching app user.
                  </p>
                </div>
              )}

              {/* Matched Customers List */}
              {syncResults.matched > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold">Successfully Linked Customers</h3>
                  <div className="border rounded-lg max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>App User ID</TableHead>
                          <TableHead>Premium Products</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncResults.results
                          .filter(r => r.matched)
                          .map((result) => (
                            <TableRow key={result.stripeCustomerId}>
                              <TableCell className="font-medium break-all">{result.email}</TableCell>
                              <TableCell className="break-all">{result.name || '-'}</TableCell>
                              <TableCell className="font-mono text-xs break-all">{result.appUserId?.substring(0, 8)}...</TableCell>
                              <TableCell>
                                <Badge variant="default">{result.premiumProducts.length} products</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Details Sheet */}
      <Sheet open={!!selectedCustomerId} onOpenChange={(open) => !open && setSelectedCustomerId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {isLoadingDetails ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : customerDetails ? (
            <>
              <SheetHeader>
                <SheetTitle>Customer Details</SheetTitle>
                <SheetDescription>
                  Full customer information and subscription history
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Basic Info */}
                <div className="space-y-2">
                  <h3 className="font-semibold">Basic Information</h3>
                  <div className="space-y-1 text-sm">
                     <div className="flex justify-between items-center py-2 border-b">
                       <span className="text-muted-foreground">Customer ID</span>
                       <div className="flex items-center gap-2">
                         <a
                           href={`https://dashboard.stripe.com/customers/${customerDetails.customer.id}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="font-mono text-xs text-primary hover:underline flex items-center gap-1"
                         >
                           {customerDetails.customer.id}
                           <ExternalLink className="h-3 w-3" />
                         </a>
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => copyToClipboard(customerDetails.customer.id, 'Customer ID')}
                         >
                           {copiedId === customerDetails.customer.id ? (
                             <Check className="h-4 w-4" />
                           ) : (
                             <Copy className="h-4 w-4" />
                           )}
                         </Button>
                       </div>
                     </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Email</span>
                      <span>{customerDetails.customer.email || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Name</span>
                      <span>{customerDetails.customer.name || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Created</span>
                      <span>
                        {formatDistanceToNow(new Date(customerDetails.customer.created * 1000), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Balance</span>
                      <span>
                        {customerDetails.customer.balance === 0
                          ? '$0.00'
                          : `$${(customerDetails.customer.balance / 100).toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Subscriptions */}
                <div className="space-y-2">
                  <h3 className="font-semibold">Subscriptions ({customerDetails.subscriptions.length})</h3>
                  {customerDetails.subscriptions.length > 0 ? (
                    <div className="space-y-3">
                      {customerDetails.subscriptions.map((sub) => (
                         <Card key={sub.id}>
                           <CardContent className="pt-4 space-y-2">
                             <div className="flex justify-between items-start">
                               <div>
                                 <Badge 
                                   variant={
                                     sub.status === 'canceled' || sub.cancelAtPeriodEnd 
                                       ? 'secondary' 
                                       : sub.status === 'active' || sub.status === 'trialing'
                                       ? 'default' 
                                       : 'outline'
                                   }
                                   className={
                                     sub.status === 'canceled' || sub.cancelAtPeriodEnd
                                       ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                       : ''
                                   }
                                 >
                                   {sub.cancelAtPeriodEnd ? 'Canceled (active until end)' : sub.status}
                                 </Badge>
                               </div>
                               <a
                                 href={`https://dashboard.stripe.com/subscriptions/${sub.id}`}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="text-xs font-mono text-primary hover:underline flex items-center gap-1"
                               >
                                 {sub.id.substring(0, 12)}...
                                 <ExternalLink className="h-3 w-3" />
                               </a>
                             </div>
                            <div className="space-y-1 text-sm">
                              {sub.items.map((item, idx) => (
                                <div key={idx}>
                                  <p className="font-medium">{item.productName}</p>
                                  <p className="text-muted-foreground">
                                    {item.amount ? `$${(item.amount / 100).toFixed(2)}` : 'N/A'} / {item.interval || 'N/A'}
                                  </p>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Renews: {new Date(sub.currentPeriodEnd * 1000).toLocaleDateString()}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No subscriptions</p>
                  )}
                </div>

                {/* Payment Methods */}
                <div className="space-y-2">
                  <h3 className="font-semibold">Payment Methods ({customerDetails.paymentMethods.length})</h3>
                  {customerDetails.paymentMethods.length > 0 ? (
                    <div className="space-y-2">
                      {customerDetails.paymentMethods.map((pm) => (
                        <div key={pm.id} className="flex items-center gap-2 text-sm py-2 border-b">
                          <Badge variant="outline">{pm.type}</Badge>
                          {pm.card && (
                            <span>
                              {pm.card.brand} •••• {pm.card.last4} - Exp {pm.card.expMonth}/{pm.card.expYear}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No payment methods</p>
                  )}
                </div>

                {/* Recent Invoices */}
                <div className="space-y-2">
                  <h3 className="font-semibold">Recent Invoices ({customerDetails.invoices.length})</h3>
                  {customerDetails.invoices.length > 0 ? (
                    <div className="space-y-2">
                      {customerDetails.invoices.map((inv) => (
                        <div key={inv.id} className="flex justify-between items-center text-sm py-2 border-b">
                          <div>
                            <p className="font-medium">{inv.number || inv.id.substring(0, 12)}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(inv.created * 1000).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">${(inv.amountDue / 100).toFixed(2)}</p>
                            <Badge variant={inv.status === 'paid' ? 'default' : 'secondary'}>
                              {inv.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No invoices</p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </Card>
    </>
  );
}
